'use client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, Package, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '@/components/ui/card';
import { GLASS_CARD } from '@/components/ui/glass';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Pagination from '@/components/pages/shared/Pagination';

import AddProductDialog from '@/components/pages/dialogs/AddProductDialog';
import { useProductos, Producto } from '@/components/hooks/useProductos';
import { usePageHelp, useTourVisibility } from '@/components/hooks/usePageHelp';

import Joyride from 'react-joyride';
import getProductsTourSteps from '@/components/tour/company/ProductsTourSteps';
import {
  DLG_BTN_SECONDARY,
  DLG_BTN_DANGER,
} from '@/components/ui/dialogButtons';
import { getTourStyles } from '@/components/tour/tourStyles';

const LS_PAGE_SIZE_KEY = 'products_page_size';

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.08 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

const rowVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.25 } },
  exit: { opacity: 0, x: 20, transition: { duration: 0.18 } },
};

export default function Products() {
  const { t } = useTranslation();
  const isMobile = window.innerWidth < 640;
  const idEmpresa =
    typeof window !== 'undefined' ? localStorage.getItem('id_empresa') : null;

  const {
    productos,
    loading,
    deleteProducto,
    refetch,
    createProducto,
    updateProducto,
    uploadImagenProducto,
    removeImagenProducto,
  } = useProductos(idEmpresa || '');

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(() => {
    if (typeof window === 'undefined') return 10;
    const saved = localStorage.getItem(LS_PAGE_SIZE_KEY);
    return saved ? Number(saved) : 10;
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [productoToEdit, setProductoToEdit] = useState<Producto | null>(null);

  const productosFiltrados = useMemo(() => {
    const q = search.toLowerCase();
    return productos.filter((p) => (p.nombre ?? '').toLowerCase().includes(q));
  }, [productos, search]);

  const totalPages = Math.ceil(productosFiltrados.length / pageSize) || 1;

  const currentData = productosFiltrados.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const hayProductos = productosFiltrados.length > 0;

  const [runTour, setRunTour] = useState(false);
  const steps = getProductsTourSteps(isMobile);

  // El botón global de ayuda (arriba a la derecha) lanza el tour de esta
  // página. Se oculta mientras carga, que es cuando aún no hay tabla que
  // enseñar.
  usePageHelp(() => setRunTour(true), !loading);
  useTourVisibility(runTour);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const changePageSize = (size: number) => {
    setPageSize(size);
    setPage(1);
    localStorage.setItem(LS_PAGE_SIZE_KEY, String(size));
  };

  const openCreate = () => {
    setProductoToEdit(null);
    setOpenDialog(true);
  };

  const openEdit = (p: Producto) => {
    setProductoToEdit(p);
    setOpenDialog(true);
  };

  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    await deleteProducto(confirmDeleteId);
    refetch();
    setConfirmDeleteId(null);
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="w-full space-y-8 pb-28 lg:pb-0"
    >
      {/* HEADER — el botón de ayuda vive ahora arriba a la derecha, fijo
          y global (HelpButton), lanzado desde usePageHelp. */}
      <motion.div variants={itemVariants}>
        <div className="tour-title flex items-center text-sm text-gray-600 dark:text-gray-400">
          <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            {t('products.title')}
          </span>
        </div>
      </motion.div>

      <Joyride
        steps={steps}
        run={runTour}
        continuous
        showSkipButton
        spotlightClicks
        locale={{
          back: t('profiles.tour.back'),
          close: t('profiles.tour.close'),
          last: t('profiles.tour.last'),
          next: t('profiles.tour.next'),
          skip: t('profiles.tour.skip'),
        }}
        callback={({ status }) => {
          if (status === 'finished' || status === 'skipped') {
            setRunTour(false);
          }
        }}
        styles={getTourStyles(isMobile)}
      />

      {/* FILTRO + AÑADIR */}
      <motion.div
        variants={itemVariants}
        className="flex flex-col sm:flex-row gap-3 justify-between items-center"
      >
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder={t('products.searchPlaceholder')}
          className="tour-filter-input w-full sm:w-1/2 dark:bg-white/5 dark:border-white/10 dark:text-white"
        />

        <Button
          onClick={openCreate}
          className="rounded-lg text-white font-medium shadow-md bg-[#103254] hover:bg-[#0d2742] px-6 py-2 w-full sm:w-auto tour-add-btn transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('products.addProduct')}
        </Button>
      </motion.div>

      {/* LOADING */}
      <AnimatePresence>
        {loading && (
          <motion.div
            variants={itemVariants}
            className="text-sm text-gray-500 dark:text-gray-400"
          >
            {t('products.loading')}
          </motion.div>
        )}
      </AnimatePresence>

      {/* EMPTY */}
      {!loading && !hayProductos && (
        <Card className={`rounded-2xl ${GLASS_CARD}`}>
          <CardContent className="py-16 text-center">
            <Package className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('products.noProducts')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              {t('products.noProductsText')}
            </p>

            <Button
              onClick={openCreate}
              className="mt-6 rounded-lg text-white px-6 py-2 bg-[#103254] hover:bg-[#0d2742] transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('products.createFirst')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ===== VISTA MÓVIL: TARJETAS ===== */}
      {hayProductos && (
        <div className="block lg:hidden w-full">
          <AnimatePresence>
            <div className="w-full space-y-4">
              {currentData.map((p) => (
                <motion.div
                  key={p.id_producto}
                  variants={rowVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="w-full"
                >
                  <Card className={`w-full rounded-xl ${GLASS_CARD} overflow-hidden`}>
                    <CardContent className="p-4 w-full">
                      <div className="flex items-start gap-3 w-full min-w-0">
                        {/* IMAGEN */}
                        <div
                          className="w-16 h-16 shrink-0 rounded-md border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5 overflow-hidden bg-center bg-cover"
                          style={
                            p.imagen_principal
                              ? {
                                  backgroundImage: `url(${p.imagen_principal})`,
                                }
                              : undefined
                          }
                        >
                          {!p.imagen_principal && (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                        </div>

                        {/* CONTENIDO */}
                        <div className="flex-1 min-w-0">
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 dark:text-white truncate">
                              {p.nombre || '—'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                              {p.descripcion || '—'}
                            </p>
                          </div>

                          <div className="mt-3 flex items-center justify-between w-full">
                            <span
                              className={`px-2 py-0.5 rounded-md text-[11px] font-semibold shrink-0 ${
                                p.activo
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                              }`}
                            >
                              {p.activo
                                ? t('products.activeYes')
                                : t('products.activeNo')}
                            </span>

                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {p.precio != null
                                ? `${p.precio.toFixed(2)} €`
                                : '—'}
                            </p>

                            <div className="flex items-center gap-1 shrink-0">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEdit(p)}
                                className="tour-edit-btn-mobile hover:bg-gray-100 dark:hover:bg-white/10"
                              >
                                <Pencil className="w-4 h-4 text-[#103254] dark:text-blue-400" />
                              </Button>

                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  setConfirmDeleteId(p.id_producto)
                                }
                                className="tour-delete-btn-mobile hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </AnimatePresence>
        </div>
      )}

      {/* ===== VISTA DESKTOP: TABLA ===== */}
      {hayProductos && (
        <Card className={`hidden lg:block rounded-2xl ${GLASS_CARD}`}>
          <CardContent className="p-0">
            <table className="w-full text-sm text-gray-900 dark:text-gray-100">
              <thead className="tour-table-head bg-gray-100 dark:bg-white/5 text-xs uppercase text-gray-700 dark:text-gray-300">
                <tr>
                  <th className="px-4 py-3 text-left">
                    {t('products.colProduct')}
                  </th>
                  <th className="px-4 py-3 text-left w-32">
                    {t('products.colPrice')}
                  </th>
                  <th className="px-4 py-3 text-left">
                    {t('products.colStatus')}
                  </th>
                  <th className="px-4 py-3 w-24">{t('products.colActions')}</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 dark:divide-white/10">
                {currentData.map((p) => (
                  <tr
                    key={p.id_producto}
                    className="bg-white dark:bg-transparent hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-4">
                        <div
                          className="w-14 h-14 shrink-0 rounded-md border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5 overflow-hidden bg-center bg-cover"
                          style={
                            p.imagen_principal
                              ? {
                                  backgroundImage: `url(${p.imagen_principal})`,
                                }
                              : undefined
                          }
                        >
                          {!p.imagen_principal && (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                        </div>

                        <div className="min-w-0">
                          <p className="font-semibold text-gray-800 dark:text-white truncate">
                            {p.nombre}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                            {p.descripcion}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-4 text-gray-800 dark:text-white">
                      {p.precio != null ? `${p.precio.toFixed(2)} €` : '—'}
                    </td>

                    <td className="px-4 py-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold tour-activo-badge ${
                          p.activo
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        }`}
                      >
                        {p.activo
                          ? t('products.activeYes')
                          : t('products.activeNo')}
                      </span>
                    </td>

                    <td className="px-4 py-4">
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEdit(p)}
                          className="tour-edit-btn-desktop hover:bg-gray-100 dark:hover:bg-white/10"
                        >
                          <Pencil className="w-4 h-4 text-[#103254] dark:text-blue-400" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setConfirmDeleteId(p.id_producto)}
                          className="tour-delete-btn-desktop hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* PAGINACIÓN */}
      {hayProductos && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalItems={productosFiltrados.length}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={changePageSize}
          itemLabel="productos"
          className="tour-pagination"
        />
      )}

      {/* DIALOG CONFIRMAR ELIMINACIÓN (bottom sheet) */}
      <Dialog
        open={!!confirmDeleteId}
        onOpenChange={(o) => !o && setConfirmDeleteId(null)}
      >
        <DialogContent
          className="
            z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
            [&>button:last-child]:hidden
            flex flex-col
            !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
            w-full max-w-none rounded-t-2xl rounded-b-none
            data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
            sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
            sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:rounded-xl sm:shadow-xl
          "
        >
          <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl font-semibold text-red-600 dark:text-red-400 pr-10">
                {t('products.deleteTitle')}
              </DialogTitle>
              <DialogDescription className="text-xs text-gray-500 dark:text-gray-400">
                {t('products.deleteDescription')}
              </DialogDescription>
            </DialogHeader>
            <button
              onClick={() => setConfirmDeleteId(null)}
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-4 sm:px-6 pb-5">
            <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {t('products.deleteConfirm')}
              </p>
            </div>
          </div>

          <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
            <Button
              variant="outline"
              onClick={() => setConfirmDeleteId(null)}
              className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
            >
              {t('products.cancel')}
            </Button>

            <Button
              onClick={handleDelete}
              className={`w-full sm:flex-1 ${DLG_BTN_DANGER}`}
            >
              {t('products.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG CREAR/EDITAR */}
      <AddProductDialog
        open={openDialog}
        productoToEdit={productoToEdit}
        onOpenChange={(open) => {
          setOpenDialog(open);
          if (!open) setProductoToEdit(null);
        }}
        onSave={async () => {
          await refetch();
          setOpenDialog(false);
          setProductoToEdit(null);
        }}
        createProducto={createProducto}
        updateProducto={updateProducto}
        uploadImagenProducto={uploadImagenProducto}
        removeImagenProducto={removeImagenProducto}
      />
    </motion.div>
  );
}